//@flow

import {endpoints} from '../api';
import {useState, useEffect} from 'react';
import {
  createCrmSegmentService,
  createCrmSegmentTaskService,
  listCrmSegmentsService,
  getCrmSegmentByIdService,
  getCrmSegmentTaskService,
  editCrmSegmentService,
} from '../promises';
import {uploadFileService} from '../../file-storage/promises';

import type {CrmSegmentListItem, CrmSegmentTask, CrmSegment} from '../promises';
import type {CrmSegmentFilter} from '../../../entities/crm/segment';
import type {Pagination} from '../../../entities';

export function useCrmSegmentTemplates() {
  return {
    crmSegmentTemplateLinkCreate: endpoints.GET_CRM_SEGMENT_TEMPLATE_FOR_CREATE,
    crmSegmentTemplateLinkUpdate: endpoints.GET_CRM_SEGMENT_TEMPLATE_FOR_UPDATE,
  };
}

export function useCrmSegmentActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createManualSegment = async (segmentName: string, file: File) => {
    setLoading(true);

    try {
      const result = await createCrmSegmentService({
        enabled: true,
        name: segmentName,
        segmentType: 'MANUAL',
      });

      const fileResult = await uploadFileService(file, {
        filePrefix: 'winehouse/crm-segment',
        resourceType: 'XLSX',
        temp: true,
      });
      const fileId = fileResult[0].id;

      const taskResponse = createCrmSegmentTaskService({
        fileId,
        segmentId: result.id,
        taskType: 'UPLOAD_CONTACTS',
      });

      return taskResponse;
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const updateManualSegment = async (
    segmentId: string,
    segmentName: string,
    file?: File | null
  ) => {
    setLoading(true);

    try {
      const result = await editCrmSegmentService(segmentId, {
        name: segmentName,
      });

      let fileId = null;
      if (file) {
        const fileResult = await uploadFileService(file, {
          filePrefix: 'winehouse/crm-segment',
          resourceType: 'XLSX',
          temp: true,
        });
        fileId = fileResult[0].id;

        const taskResponse = await createCrmSegmentTaskService({
          fileId,
          segmentId: result.id,
          taskType: 'UPLOAD_CONTACTS',
        });

        return taskResponse;
      }

      return result;
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const createDynamicSegment = async (
    segmentName: string,
    filters: CrmSegmentFilter
  ) => {
    setLoading(true);

    try {
      const result = await createCrmSegmentService({
        enabled: true,
        name: segmentName,
        segmentType: 'AUTOMATIC',
        filters,
      });

      return result;
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const editSegment = async (segmentId: string, segmentName: string) => {
    setLoading(true);

    try {
      const result = await editCrmSegmentService(segmentId, {
        name: segmentName,
      });

      return result;
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  return {
    createManualSegment,
    editSegment,
    updateManualSegment,
    createDynamicSegment,
    loading,
    error,
  };
}

const defaultPagination = {
  page: 0,
  size: 8,
  total: 0,
};

type Props = {
  page?: number,
  size?: number,
};

const rawData = [
  {
    id: 'SEG-3B42734A',
    storeId: 'S-A4E538E4',
    enabled: true,
    name: 'test-20241216-001',
    segmentType: 'MANUAL',
    filters: {
      filterType: '',
      conditions: null,
      subFiltersOperator: '',
      subFilters: null,
    },
    lastSyncAt: null,
    createdAt: '2024-12-16T16:37:55.196719Z',
    type: 'segment',
  },
  {
    id: 'SEG-5B34BE4A',
    storeId: 'S-A4E538E4',
    enabled: true,
    name: 'test-20241213-002',
    segmentType: 'AUTOMATIC',
    filters: {
      filterType: 'ORDER_CREATED',
      conditions: {
        days: 30,
        hasPurchased: true,
      },
      subFiltersOperator: '',
      subFilters: [
        {
          filterType: 'ORDER_AMOUNT',
          conditions: {
            amount: 500,
            operator: 'gt',
          },
          subFiltersOperator: '',
          subFilters: null,
        },
        {
          filterType: 'ORDER_ITEM_CATEGORY',
          conditions: {
            categories: ['Galletas'],
            included: true,
          },
          subFiltersOperator: '',
          subFilters: null,
        },
        {
          filterType: 'CRM_CUSTOMER_CATEGORY',
          conditions: {
            categories: ['Lima Moderna'],
          },
          subFiltersOperator: '',
          subFilters: null,
        },
      ],
    },
    lastSyncAt: null,
    createdAt: '2024-12-13T20:35:37.094082Z',
    type: 'segment',
  },
  {
    id: 'SEG-E5747E48',
    storeId: 'S-A4E538E4',
    enabled: true,
    name: 'test-20241213-001',
    segmentType: 'MANUAL',
    filters: {
      filterType: '',
      conditions: null,
      subFiltersOperator: '',
      subFilters: null,
    },
    lastSyncAt: null,
    createdAt: '2024-12-13T20:33:09.29812Z',
    type: 'segment',
  },
  {
    id: 'SEG-2874DA4D',
    storeId: 'S-A4E538E4',
    enabled: true,
    name: 'Test Gonzalo',
    segmentType: 'AUTOMATIC',
    filters: {
      filterType: 'ORDER_CREATED',
      conditions: {
        days: 30,
        hasPurchased: true,
      },
      subFiltersOperator: '',
      subFilters: null,
    },
    lastSyncAt: null,
    createdAt: '2024-12-12T16:14:21.213567Z',
    type: 'segment',
  },
  {
    id: 'SEG-8E94A343',
    storeId: 'S-A4E538E4',
    enabled: true,
    name: 'manual-003',
    segmentType: 'MANUAL',
    filters: {
      filterType: '',
      conditions: null,
      subFiltersOperator: '',
      subFilters: null,
    },
    lastSyncAt: null,
    createdAt: '2024-11-28T19:17:52.396244Z',
    type: 'segment',
  },
  {
    id: 'SEG-8D94534B',
    storeId: 'S-A4E538E4',
    enabled: true,
    name: 'manual-002',
    segmentType: 'MANUAL',
    filters: {
      filterType: '',
      conditions: null,
      subFiltersOperator: '',
      subFilters: null,
    },
    lastSyncAt: null,
    createdAt: '2024-11-28T18:45:35.137073Z',
    type: 'segment',
  },
  {
    id: 'SEG-95A4E946',
    storeId: 'S-A4E538E4',
    enabled: true,
    name: 'manual-001',
    segmentType: 'MANUAL',
    filters: {
      filterType: '',
      conditions: null,
      subFiltersOperator: '',
      subFilters: null,
    },
    lastSyncAt: null,
    createdAt: '2024-11-28T18:34:44.063661Z',
    type: 'segment',
  },
  {
    id: 'SEG-EA74954D',
    storeId: 'S-A4E538E4',
    enabled: true,
    name: 'segment-001',
    segmentType: 'AUTOMATIC',
    filters: {
      filterType: 'ORDER_CREATED',
      conditions: {
        days: 5,
        hasPurchased: true,
      },
      subFiltersOperator: '',
      subFilters: null,
    },
    lastSyncAt: null,
    createdAt: '2024-11-28T18:27:16.424672Z',
    type: 'segment',
  },
];

export function useCrmSegments(props?: Props) {
  const {page: initialPage = 0, size: initialSize = 8} = props || {};
  const [segments, setSegments] = useState<CrmSegmentListItem[]>(rawData);

  const [page, setPage] = useState<number>(initialPage);
  const [size, setSize] = useState<number>(initialSize);
  const [pagination, setPagination] = useState<Pagination>(defaultPagination);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);

    listCrmSegmentsService({
      page,
      size,
    })
      .then(response => {
        setSegments(response.data);
        if (response.pagination) {
          setPagination(response.pagination);
        }
      })
      .catch(error => {
        setError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [page, size]);

  return {
    segments,
    loading,
    error,
    pagination,
    setSize,
    setPage,
  };
}

export function useSegmentDetail(segmentId: string) {
  const [segment, setSegment] = useState<CrmSegment | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (segmentId === 'new') {
      setSegment(null);
      return;
    }

    setLoading(true);
    getCrmSegmentByIdService(segmentId)
      .then(response => {
        setSegment(response);
      })
      .catch(error => {
        setError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [segmentId]);

  return {
    segment,
    loading,
    error,
  };
}

export function useSegmentTasks(segmentId: string) {
  const [segmentTasks, setSegmentTasks] = useState<CrmSegmentTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (segmentId === 'new') {
      setSegmentTasks([]);
      return;
    }

    setLoading(true);

    getCrmSegmentTaskService(segmentId)
      .then(response => {
        setSegmentTasks(response);
      })
      .catch(error => {
        setError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [segmentId]);

  return {
    segmentTasks,
    loading,
    error,
  };
}
